/**
 * Trade Ideas Table Row — single row rendering
 */
import React from 'react';
import { Box, Typography, TableCell, TableRow, Tooltip, alpha } from '@mui/material';
import { TrendingUp, TrendingDown, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const fmtPrice = p => p ? new Intl.NumberFormat('vi-VN').format(Math.round(p)) : 'N/A';
const setupClr = s => s === 'breakout' ? '#10b981' : s === 'pullback' ? '#3b82f6' : '#94a3b8';
const riskClr = r => r <= 0.02 ? '#10b981' : r <= 0.05 ? '#f59e0b' : '#ef4444';
const isOld = d => d ? new Date(d) < new Date(Date.now() - 7 * 864e5) : false;

const TradeIdeasTableRow = ({ idea, getIdeaSector }) => {
  const { t } = useTranslation();
  const hr = idea.is_high_risk;
  const rp = idea.risk_pct || 0;
  const sc = setupClr(idea.reason);

  return (
    <TableRow hover sx={{ bgcolor: hr ? alpha('#f59e0b', 0.03) : 'inherit', '&:hover': { bgcolor: hr ? alpha('#f59e0b', 0.06) : alpha('#667eea', 0.02) } }}>
      {/* Ticker */}
      <TableCell sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hr && <Tooltip title={t('tradingDecision.tradeIdeasTable.highRiskTooltip', 'High risk')}><Warning sx={{ fontSize: 14, color: '#f59e0b' }} /></Tooltip>}
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{idea.ticker}</Typography>
        </Box>
      </TableCell>

      {/* Date */}
      <TableCell sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{idea.date?.split('T')[0] || 'N/A'}</Typography>
      </TableCell>

      {/* Entry */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: isOld(idea.date) ? '#f59e0b' : '#1e293b' }}>
          {fmtPrice(idea.entry)}
        </Typography>
      </TableCell>

      {/* Stop */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{fmtPrice(idea.stop)}</Typography>
      </TableCell>

      {/* Risk % */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: riskClr(rp), bgcolor: alpha(riskClr(rp), 0.08), px: 0.6, py: 0.2, borderRadius: 0.5, display: 'inline-block' }}>
          {(rp * 100).toFixed(2)}%
        </Typography>
      </TableCell>

      {/* Size */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#475569' }}>{((idea.size_nav_frac || 0) * 100).toFixed(2)}%</Typography>
      </TableCell>

      {/* Setup */}
      <TableCell sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: sc, bgcolor: alpha(sc, 0.08), px: 0.6, py: 0.15, borderRadius: 0.5, display: 'inline-block', textTransform: 'capitalize' }}>
          {idea.reason === 'breakout' ? t('tradingDecision.tradeIdeasTable.breakout', 'Breakout') : idea.reason === 'pullback' ? t('tradingDecision.tradeIdeasTable.pullback', 'Pullback') : idea.reason || 'N/A'}
        </Typography>
      </TableCell>

      {/* RS */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
          {idea.rs > 1 ? <TrendingUp sx={{ fontSize: 12, color: '#10b981' }} /> : <TrendingDown sx={{ fontSize: 12, color: '#ef4444' }} />}
          <Typography sx={{ fontSize: '0.72rem', color: '#475569' }}>{idea.rs?.toFixed(2) || 'N/A'}</Typography>
        </Box>
      </TableCell>

      {/* Money Flow */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: (idea.money_flow || 0) > 0 ? '#10b981' : '#ef4444' }}>
          {idea.money_flow ? fmtPrice(idea.money_flow) : 'N/A'}
        </Typography>
      </TableCell>

      {/* Sector */}
      <TableCell sx={{ py: 0.5, px: 1, borderColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{getIdeaSector(idea) || 'N/A'}</Typography>
      </TableCell>
    </TableRow>
  );
};

export default TradeIdeasTableRow;
